/* assets/cart-progress.js
 * <cart-progress> renders the free shipping + gift bar in the cart drawer.
 * Renders into shadow DOM so Horizon's cart re-rendering can't wipe it;
 * re-renders whenever the server updates its data attributes.
 */
const COPY = {
  de: {
    lead: (x) => `Noch <strong>${x}</strong> bis `,
    parts: {
      ship: ['zum kostenlosen Versand', 'kostenloser Versand'],
      g1: ['zu einer Gratis-Probe deiner Wahl', '1 Gratis-Probe deiner Wahl'],
      g2: ['zur 2. Gratis-Probe', '2. Gratis-Probe'],
      g3: ['zu einer Gratis-Mini-Größe deiner Wahl (statt der Proben)', 'Gratis-Mini-Größe deiner Wahl (statt der Proben)'],
    },
    shipDone: 'Kostenloser Versand ist dabei. ',
    allDone: (ship) => ship
      ? 'Kostenloser Versand und eine Gratis-Mini-Größe deiner Wahl sind dabei'
      : 'Deine Gratis-Mini-Größe deiner Wahl ist dabei',
  },
  en: {
    lead: (x) => `<strong>${x}</strong> to go for `,
    parts: {
      ship: ['free shipping', 'free shipping'],
      g1: ['1 free sample of your choice', '1 free sample of your choice'],
      g2: ['a 2nd free sample', 'a 2nd free sample'],
      g3: ['a free mini size of your choice (instead of the samples)', 'a free mini size of your choice (instead of the samples)'],
    },
    shipDone: 'Free shipping unlocked. ',
    allDone: (ship) => ship
      ? 'Free shipping and a free mini size of your choice unlocked'
      : 'Free mini size of your choice unlocked',
  },
};

const STYLES = `
  :host { display: block; padding: 12px var(--cart-drawer-padding, 0) 16px; }
  .text { margin: 0 0 10px; font-size: 14px; line-height: 1.4; }
  .track { position: relative; height: 6px; border-radius: 3px;
    background: rgb(var(--color-foreground-rgb, 0 0 0) / 0.12); }
  .fill { height: 100%; border-radius: 3px; background: #69FF47; transition: width 0.3s ease; }
  .marker { position: absolute; top: 50%; width: 12px; height: 12px; border-radius: 50%;
    transform: translate(-50%, -50%); box-sizing: border-box;
    background: var(--color-background, #fff);
    border: 2px solid rgb(var(--color-foreground-rgb, 0 0 0) / 0.25); }
  .marker.last { transform: translate(-100%, -50%); }
  .marker.reached { background: #69FF47; border-color: #111110; }
  @media (prefers-reduced-motion: reduce) { .fill { transition: none; } }
`;

class CartProgress extends HTMLElement {
  static observedAttributes = ['data-total', 'data-currency', 'data-ship', 'data-gifts', 'data-locale'];

  connectedCallback() {
    if (!this.shadowRoot) this.attachShadow({ mode: 'open' });
    this.render();
  }

  attributeChangedCallback() {
    if (this.shadowRoot) this.render();
  }

  money(cents, currency, locale) {
    const value = cents / 100;
    const whole = Number.isInteger(cents / 100);
    const intl = locale === 'en' ? (currency === 'USD' ? 'en-US' : 'en-GB') : 'de-DE';
    return new Intl.NumberFormat(intl, {
      style: 'currency',
      currency,
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2,
    }).format(value);
  }

  render() {
    const d = this.dataset;
    const total = parseInt(d.total, 10) || 0;
    const currency = d.currency || 'EUR';
    const locale = d.locale === 'en' ? 'en' : 'de';
    const copy = COPY[locale];
    const rate = currency === 'EUR' ? 1 : parseFloat(window.Shopify?.currency?.rate) || 1;
    const conv = (eurCents) => Math.round(eurCents * rate);

    // Milestones in the customer's currency
    const items = [];
    const shipEur = parseInt(d.ship, 10) || 0;
    if (shipEur > 0) items.push({ amt: conv(shipEur), key: 'ship' });
    (d.gifts || '').split(',').map((n) => parseInt(n, 10)).filter(Boolean)
      .forEach((g, i) => items.push({ amt: conv(g), key: `g${i + 1}` }));
    if (!items.length) { this.shadowRoot.innerHTML = ''; return; }

    const order = { ship: 0, g1: 1, g2: 2, g3: 3 };
    items.sort((a, b) => a.amt - b.amt || order[a.key] - order[b.key]);
    const max = items[items.length - 1].amt;
    const amounts = [...new Set(items.map((i) => i.amt))];

    // Message
    const next = amounts.find((a) => total < a);
    let text;
    if (next === undefined) {
      text = copy.allDone(shipEur > 0);
    } else {
      const group = items.filter((i) => i.amt === next);
      const missing = this.money(next - total, currency, locale);
      const phrase = copy.lead(missing) + group
        .map((g, idx) => copy.parts[g.key][idx === 0 ? 0 : 1])
        .join(' + ');
      const shipItem = items.find((i) => i.key === 'ship');
      const shipReached = shipItem && total >= shipItem.amt;
      text = (shipReached ? copy.shipDone : '') + phrase;
    }

    const pct = Math.min(100, (total / max) * 100);
    const markers = amounts.map((a, i) => {
      const pos = (a / max) * 100;
      const cls = ['marker', total >= a ? 'reached' : '', i === amounts.length - 1 ? 'last' : ''].join(' ');
      return `<span class="${cls}" style="left:${pos}%" title="${this.money(a, currency, locale)}"></span>`;
    }).join('');

    this.shadowRoot.innerHTML = `
      <style>${STYLES}</style>
      <div role="status" aria-live="polite">
        <p class="text">${text}</p>
        <div class="track"><div class="fill" style="width:${pct}%"></div>${markers}</div>
      </div>`;
  }
}

if (!customElements.get('cart-progress')) customElements.define('cart-progress', CartProgress);
